import { useInvoice } from '@/hooks/invoice/useInvoice';
import { Box, Button, CircularProgress, Modal, Stack, Typography } from '@mui/material';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import { useConfig } from '@/hooks/useConfig';
import { getStripe } from '@/utils/stripe';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReferenceDetails from './ReferenceDetails';

type StripePaymentProps = {
  open: boolean;
  clientSecret: string | null;
  publishableKey: string | null;
  returnUrl: string | null;
  onSuccess: () => void;
  onClose: () => void;
  onCancel: () => void;
};

type StripePaymentFormProps = {
  clientSecret: string;
  returnUrl: string;
  onSuccess: () => void;
  onCancel: () => void;
};

const StripePaymentForm = ({ clientSecret, returnUrl, onSuccess, onCancel }: StripePaymentFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isSetupIntent = clientSecret.startsWith('seti_');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setErrorMessage(null);

    const { error } = isSetupIntent
      ? await stripe.confirmSetup({
          elements,
          confirmParams: { return_url: returnUrl },
          redirect: 'if_required'
        })
      : await stripe.confirmPayment({
          elements,
          confirmParams: { return_url: returnUrl },
          redirect: 'if_required'
        });

    if (error) {
      setErrorMessage(error.message ?? t('errors.something-went-wrong'));
      setIsProcessing(false);
    } else {
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <Stack gap={2} sx={{ width: '100%' }}>
        <PaymentElement />

        {errorMessage && (
          <Typography variant='body3' color='error'>
            {errorMessage}
          </Typography>
        )}

        <Stack gap={1} alignItems='center' sx={{ width: '100%' }}>
          <Button type='submit' variant='contained' color='primary' fullWidth disabled={!stripe || isProcessing}>
            {isProcessing ? (
              <Stack direction='row' gap={1} alignItems='center'>
                <CircularProgress size={16} color='inherit' />
                <span>{t('page.pay.stripe-modal.processing')}</span>
              </Stack>
            ) : isSetupIntent ? (
              t('page.pay.stripe-modal.register-button')
            ) : (
              t('page.pay.stripe-modal.pay-button')
            )}
          </Button>

          <Typography marginTop={1} variant='body2' color='primary' sx={{ cursor: 'pointer' }} onClick={onCancel}>
            {t('buttons.cancel')}
          </Typography>
        </Stack>
      </Stack>
    </form>
  );
};

const StripePayment = ({
  open,
  clientSecret,
  publishableKey,
  returnUrl,
  onSuccess,
  onClose,
  onCancel
}: StripePaymentProps) => {
  const { t } = useTranslation();
  const { invoice } = useInvoice();
  const isSetupIntent = !!clientSecret && clientSecret.startsWith('seti_');
  const { urls } = useConfig();

  const stripePromise = useMemo(
    () => (publishableKey ? getStripe(publishableKey, urls.stripe) : null),
    [publishableKey, urls.stripe]
  );

  const options: StripeElementsOptions = {
    clientSecret: clientSecret ?? undefined,
    locale: 'sv',
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#5A87AA',
        fontFamily: '"Noto Sans", sans-serif',
        borderRadius: '4px'
      }
    }
  };

  return (
    <Modal open={open} onClose={onClose} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          width: '90%',
          maxWidth: '480px',
          p: 3,
          boxShadow: 24
        }}
      >
        <Stack gap={2}>
          <Typography variant='h2' fontWeight='bold' align='center'>
            {t('page.pay.stripe-modal.title')}
          </Typography>

          {!isSetupIntent && invoice && <ReferenceDetails invoice={invoice} />}

          {stripePromise && clientSecret && returnUrl ? (
            <Box sx={{ width: '100%' }}>
              <Elements stripe={stripePromise} options={options}>
                <StripePaymentForm
                  clientSecret={clientSecret}
                  returnUrl={returnUrl}
                  onSuccess={onSuccess}
                  onCancel={onCancel}
                />
              </Elements>
            </Box>
          ) : (
            <Stack alignItems='center' py={4}>
              <CircularProgress />
            </Stack>
          )}
        </Stack>
      </Box>
    </Modal>
  );
};

export default StripePayment;
